import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../config/api';
import { sortRegions } from '../utils/sort';
import PageLayout from '../components/PageLayout';
import EnrichmentEditor from '../components/EnrichmentEditor';
import AdminSearchPicker from '../components/AdminSearchPicker';
import GroupedVenueSearchPicker, { type Venue } from '../components/GroupedVenueSearchPicker';
import './EventsPage.css';

// Simple glyphicon-style icons using CSS
const IconEdit = () => <span className="glyphicon-icon">✎</span>;
const IconUsers = () => <span className="glyphicon-icon">⚥</span>;
const IconTicket = () => <span className="glyphicon-icon">⊞</span>;
const IconDelete = () => <span className="glyphicon-icon">✕</span>;

interface ManualFieldUpdate {
  field_name: string;
  old_value: any;
  new_value: any;
  mod_date: string;
}

interface Event {
  id: string;
  venue_id: string;
  venue_name?: string;
  venue_city?: string;
  venue_state?: string;
  name: string;
  event_date: string;
  start_date_time?: string;
  end_date_time?: string;
  timezone?: string;
  status?: string;
  headliner_id?: string;
  supporting_artist_ids?: string[];
  source?: string; // "ticketmaster", "etix", "etix-affiliate", "etix-public", "eventim", "scraper", or undefined (none)
  last_updated?: string;
  created_at?: string;
  manually_updated_fields?: ManualFieldUpdate[];
  is_deleted?: boolean;
}

interface Artist {
  id: string;
  name: string;
}

interface EventArtist {
  event_id: string;
  artist_id: string;
  artist_name?: string; // Populated by API when listing event artists
  is_headliner: boolean;
  order: number;
  last_updated?: string;
  created_at?: string;
}

const EventsPage: React.FC = () => {
  const { token } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [eventsListLoaded, setEventsListLoaded] = useState(false);
  const regionRefetchBaseline = useRef<string | null>(null);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [formData, setFormData] = useState<Partial<Event>>({});
  const [showEnrichmentEditor, setShowEnrichmentEditor] = useState(false);
  const [enrichmentEntityId, setEnrichmentEntityId] = useState<string>('');
  const [enrichmentType, setEnrichmentType] = useState<'ticketmaster' | 'etix' | 'eventim'>('ticketmaster');
  const [showArtistsModal, setShowArtistsModal] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string>('');
  const [eventArtists, setEventArtists] = useState<EventArtist[]>([]);
  const [loadingArtists, setLoadingArtists] = useState(false);
  const [sortColumn, setSortColumn] = useState<string>('event_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [dateFilter, setDateFilter] = useState<'upcoming' | 'past' | 'all'>('upcoming');
  const [sourceFilter, setSourceFilter] = useState<string>('all'); // 'all', 'ticketmaster', 'etix', 'eventim', 'scraper', 'none'
  const [regionFilter, setRegionFilter] = useState<string>('all');
  const [venueFilter, setVenueFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [regions, setRegions] = useState<Array<{ region_name: string; metro_name: string }>>([]);
  const [eventsCount, setEventsCount] = useState<number>(0);
  const [regionsCount, setRegionsCount] = useState<number>(0);
  const [venueFilterLabel, setVenueFilterLabel] = useState<string>('');
  const [modalVenueDetail, setModalVenueDetail] = useState<Venue | null>(null);
  const [showDeleted, setShowDeleted] = useState<boolean>(false);
  const [unlockingField, setUnlockingField] = useState<{ eventId: string; fieldName: string } | null>(null);
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  const [showRawData, setShowRawData] = useState(false);
  const [pendingAddArtist, setPendingAddArtist] = useState<Artist | null>(null);

  useEffect(() => {
    if (token) {
      loadRegions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const getAdminEventsUrl = useCallback(() => {
    const params = new URLSearchParams();
    if (regionFilter !== 'all') params.set('region', regionFilter);
    if (venueFilter !== 'all') params.set('venue_id', venueFilter);
    const q = params.toString();
    return q ? `/admin/events?${q}` : '/admin/events';
  }, [regionFilter, venueFilter]);

  const pullFullEventsPageData = useCallback(async () => {
    if (!token) return;
    const url = getAdminEventsUrl();
    const eventsRes = await apiRequest<{ events: Event[] | null; count: number }>(url, token);
    setEvents(eventsRes.events || []);
    setEventsCount(eventsRes.count || 0);
    setError('');
  }, [token, getAdminEventsUrl]);

  const fetchArtistPickerPage = useCallback(
    async (q: string, lastKey?: string) => {
      if (!token) return { items: [] as Artist[], lastKey: undefined as string | undefined };
      const params = new URLSearchParams();
      params.set('limit', '50');
      if (q.trim().length >= 2) params.set('q', q.trim());
      if (lastKey) params.set('last_key', lastKey);
      const r = await apiRequest<{ artists: Artist[] | null; count: number; last_key?: string }>(
        `/admin/artists?${params.toString()}`,
        token
      );
      return { items: r.artists || [], lastKey: r.last_key || undefined };
    },
    [token]
  );

  const refreshEventsListOnly = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const url = getAdminEventsUrl();
      const response = await apiRequest<{ events: Event[] | null; count: number }>(url, token);
      setEvents(response.events || []);
      setEventsCount(response.count || 0);
      setEventsListLoaded(true);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to load events');
      setEvents([]);
      setEventsCount(0);
    } finally {
      setLoading(false);
    }
  }, [token, getAdminEventsUrl]);

  useEffect(() => {
    if (!token) return;
    if (!eventsListLoaded) {
      regionRefetchBaseline.current = regionFilter;
      return;
    }
    if (regionRefetchBaseline.current === regionFilter) return;
    regionRefetchBaseline.current = regionFilter;
    void refreshEventsListOnly();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionFilter, token, eventsListLoaded]);

  useEffect(() => {
    if (!showForm || !formData.venue_id || !token) {
      setModalVenueDetail(null);
      return;
    }
    const vid = formData.venue_id;
    let cancelled = false;
    void (async () => {
      try {
        const v = await apiRequest<Venue>(`/admin/venues/${encodeURIComponent(vid)}`, token);
        if (!cancelled) setModalVenueDetail(v);
      } catch {
        if (!cancelled) setModalVenueDetail(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showForm, formData.venue_id, token]);

  useEffect(() => {
    if (!showArtistsModal) {
      setPendingAddArtist(null);
    }
  }, [showArtistsModal]);

  const handleLoadEvents = async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      await pullFullEventsPageData();
      setEventsListLoaded(true);
    } catch (err: any) {
      setError(err.message || 'Failed to load events');
      setEvents([]);
      setEventsCount(0);
    } finally {
      setLoading(false);
    }
  };

  const loadRegions = async () => {
    if (!token) return;
    try {
      const response = await apiRequest<{ regions: Array<{ region_name: string; metro_name: string }> | null; count: number }>(
        '/admin/regions',
        token
      );
      setRegions(sortRegions(response.regions || []));
      setRegionsCount(response.count || 0);
    } catch (err: any) {
      console.error('Failed to load regions:', err);
      setRegions([]);
      setRegionsCount(0);
    }
  };

  const loadEventArtists = async (eventId: string) => {
    if (!token) return;
    try {
      setLoadingArtists(true);
      setError(''); // Clear previous errors
      const response = await apiRequest<EventArtist[]>(
        `/admin/events/${eventId}/artists`,
        token
      );
      console.log('Event artists response:', response);
      console.log('Event artists count:', response?.length);
      setEventArtists(Array.isArray(response) ? response : []);
    } catch (err: any) {
      console.error('Error loading event artists:', err);
      setError(err.message || 'Failed to load event artists');
      setEventArtists([]);
    } finally {
      setLoadingArtists(false);
    }
  };

  const handleCreate = () => {
    setEditingEvent(null);
    setFormData({});
    setShowForm(true);
    setShowRawData(false);
  };

  const handleEdit = (event: Event) => {
    setEditingEvent(event);
    setFormData(event);
    setShowForm(true);
    setShowRawData(false);
  };

  const handleDelete = async (id: string) => {
    if (!token || !window.confirm('Are you sure you want to delete this event? This will soft-delete it (mark as deleted).')) {
      return;
    }
    try {
      await apiRequest(`/admin/events/${id}`, token, { method: 'DELETE' });
      await refreshEventsListOnly();
    } catch (err: any) {
      setError(err.message || 'Failed to delete event');
    }
  };

  const handleHardDelete = async (id: string) => {
    if (!token || !window.confirm('Are you sure you want to permanently delete this event? This cannot be undone and will allow ingestion to recreate it.')) {
      return;
    }
    try {
      await apiRequest(`/admin/events/${id}/hard`, token, { method: 'DELETE' });
      await refreshEventsListOnly();
    } catch (err: any) {
      setError(err.message || 'Failed to hard delete event');
    }
  };

  const handleUnlockField = async (eventId: string, fieldName: string) => {
    if (!token || !window.confirm(`Are you sure you want to unlock the "${fieldName}" field? This will allow ingestion to update it again.`)) {
      return;
    }
    try {
      setUnlockingField({ eventId, fieldName });
      await apiRequest(`/admin/events/${eventId}/unlock?field=${encodeURIComponent(fieldName)}`, token, { method: 'POST' });
      await refreshEventsListOnly();
      setUnlockingField(null);
    } catch (err: any) {
      setError(err.message || 'Failed to unlock field');
      setUnlockingField(null);
    }
  };

  const isFieldProtected = (event: Event, fieldName: string): boolean => {
    return event.manually_updated_fields?.some(update => update.field_name === fieldName) || false;
  };

  const getFieldUpdate = (event: Event, fieldName: string): ManualFieldUpdate | undefined => {
    return event.manually_updated_fields?.find(update => update.field_name === fieldName);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    if (!formData.venue_id) {
      setError('Venue is required');
      return;
    }

    try {
      // Normalize datetime strings to ensure they have 'Z' suffix for RFC3339 format
      const normalizeDateTime = (dt: string | undefined): string | undefined => {
        if (!dt) return undefined;
        // If it already ends with Z, return as-is
        if (dt.endsWith('Z')) return dt;
        // If it ends with timezone offset (e.g., -05:00), return as-is
        if (dt.match(/[+-]\d{2}:\d{2}$/)) return dt;
        // Otherwise, add Z suffix
        return dt + 'Z';
      };

      // Prepare form data (do not include artist fields; edit those via Artists popup only)
      const submitData = {
        ...formData,
        start_date_time: normalizeDateTime(formData.start_date_time),
        end_date_time: normalizeDateTime(formData.end_date_time),
      };
      // Omit headliner_id and supporting_artist_ids - artists are managed only via the Artists popup
      delete (submitData as Record<string, unknown>).headliner_id;
      delete (submitData as Record<string, unknown>).supporting_artist_ids;
      delete (submitData as Record<string, unknown>).venue_name;
      delete (submitData as Record<string, unknown>).venue_city;
      delete (submitData as Record<string, unknown>).venue_state;

      if (editingEvent) {
        await apiRequest(`/admin/events/${editingEvent.id}`, token, {
          method: 'PUT',
          body: JSON.stringify(submitData),
        });
      } else {
        await apiRequest('/admin/events', token, {
          method: 'POST',
          body: JSON.stringify(submitData),
        });
      }
      setShowForm(false);
      await refreshEventsListOnly();
    } catch (err: any) {
      setError(err.message || 'Failed to save event');
    }
  };

  const handleManageArtists = async (eventId: string) => {
    setSelectedEventId(eventId);
    setShowArtistsModal(true);
    setError(''); // Clear any previous errors
    await loadEventArtists(eventId);
  };

  const handleAddArtist = async (artistId: string, isHeadliner: boolean) => {
    if (!token || !selectedEventId) return;
    try {
      await apiRequest(`/admin/events/${selectedEventId}/artists`, token, {
        method: 'POST',
        body: JSON.stringify({
          artist_id: artistId,
          is_headliner: isHeadliner,
        }),
      });
      await loadEventArtists(selectedEventId);
      await refreshEventsListOnly();
    } catch (err: any) {
      setError(err.message || 'Failed to add artist');
    }
  };

  const handleRemoveArtist = async (artistId: string) => {
    if (!token || !selectedEventId) return;
    if (!window.confirm('Are you sure you want to remove this artist from the event?')) {
      return;
    }
    try {
      await apiRequest(`/admin/events/${selectedEventId}/artists/${artistId}`, token, {
        method: 'DELETE',
      });
      await loadEventArtists(selectedEventId);
      await refreshEventsListOnly();
    } catch (err: any) {
      setError(err.message || 'Failed to remove artist');
    }
  };

  const handleUpdateArtist = async (artistId: string, isHeadliner: boolean, order: number) => {
    if (!token || !selectedEventId) return;
    try {
      await apiRequest(`/admin/events/${selectedEventId}/artists/${artistId}`, token, {
        method: 'PUT',
        body: JSON.stringify({
          is_headliner: isHeadliner,
          order: order,
        }),
      });
      await loadEventArtists(selectedEventId);
      await refreshEventsListOnly();
    } catch (err: any) {
      setError(err.message || 'Failed to update artist');
    }
  };

  /** Display label for artist: "ArtistName [artist id]" or "[artist id]" if name unknown */
  const getArtistDisplayLabel = (artistId: string, artistName?: string) => {
    return artistName ? `${artistName} [${artistId}]` : `[${artistId}]`;
  };

  const getEventVenueDisplay = (event: Event) => {
    if (event.venue_name) {
      const loc =
        event.venue_city && event.venue_state ? ` (${event.venue_city}, ${event.venue_state})` : '';
      return `${event.venue_name}${loc}`;
    }
    return event.venue_id;
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getFilteredAndSortedEvents = () => {
    // First filter by deleted status (default: exclude deleted)
    let filtered = events;
    if (!showDeleted) {
      filtered = filtered.filter(event => !event.is_deleted);
    }
    
    // Then filter by date
    if (dateFilter !== 'all') {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Set to start of today
      
      filtered = events.filter(event => {
        if (!event.event_date) return false;
        
        const eventDate = new Date(event.event_date);
        eventDate.setHours(0, 0, 0, 0);
        
        if (dateFilter === 'upcoming') {
          return eventDate >= today;
        } else if (dateFilter === 'past') {
          return eventDate < today;
        }
        return true;
      });
    }

    // Filter by source
    if (sourceFilter !== 'all') {
      filtered = filtered.filter(event => {
        if (sourceFilter === 'none') {
          return !event.source || event.source === '';
        }
        if (sourceFilter === 'etix') {
          // Include both etix-affiliate and etix-public when filtering by "etix"
          return event.source === 'etix' || event.source === 'etix-affiliate' || event.source === 'etix-public';
        }
        if (sourceFilter === 'eventim') {
          return event.source === 'eventim';
        }
        if (sourceFilter === 'scraper') {
          return event.source === 'scraper';
        }
        return event.source === sourceFilter;
      });
    }

    // Filter by venue
    if (venueFilter !== 'all') {
      filtered = filtered.filter(event => event.venue_id === venueFilter);
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(event => {
        if (statusFilter === 'none') {
          return !event.status || event.status === '';
        }
        return event.status === statusFilter;
      });
    }


    // Then filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(event => {
        const nameMatch = event.name?.toLowerCase().includes(term);
        const idMatch = event.id?.toLowerCase().includes(term);
        const venueName = getEventVenueDisplay(event).toLowerCase();
        const venueMatch = venueName.includes(term);
        return nameMatch || idMatch || venueMatch;
      });
    }

    // Then sort
    if (!sortColumn || filtered.length === 0) return filtered;

    const sorted = [...filtered].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case 'id':
          aValue = a.id;
          bValue = b.id;
          break;
        case 'name':
          aValue = a.name?.toLowerCase() || '';
          bValue = b.name?.toLowerCase() || '';
          break;
        case 'venue':
          aValue = getEventVenueDisplay(a).toLowerCase();
          bValue = getEventVenueDisplay(b).toLowerCase();
          break;
        case 'event_date':
          aValue = a.event_date || '';
          bValue = b.event_date || '';
          break;
        case 'status':
          aValue = a.status?.toLowerCase() || '';
          bValue = b.status?.toLowerCase() || '';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const getSortIcon = (column: string) => {
    if (sortColumn !== column) return '';
    return sortDirection === 'asc' ? '▲' : '▼';
  };

  return (
    <PageLayout title="Events">
      {error && <div className="error-banner">{error}</div>}
      
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ 
          padding: '0.75rem 1rem', 
          backgroundColor: '#f8f9fa', 
          borderRadius: '4px', 
          marginBottom: '1rem',
          border: '1px solid #dee2e6'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Results:</div>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', fontSize: '0.9rem' }}>
            <span>Events ({eventsListLoaded ? eventsCount : '—'})</span>
            <span>Regions ({regionsCount})</span>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', gap: '1rem' }}>
          <div style={{ flex: 1, maxWidth: '400px' }}>
            <input
              type="text"
              placeholder="Search by name, venue, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem'
              }}
            />
          </div>
          <button type="button" onClick={() => void handleCreate()} className="btn-primary">
            Create Event
          </button>
        </div>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'auto 1fr auto', 
          gap: '0.75rem 1rem', 
          alignItems: 'center',
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          border: '1px solid #dee2e6'
        }}>
          <label style={{ fontSize: '0.9rem', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Date:</label>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="radio"
                name="dateFilter"
                value="upcoming"
                checked={dateFilter === 'upcoming'}
                onChange={(e) => setDateFilter(e.target.value as 'upcoming' | 'past' | 'all')}
              />
              <span>Upcoming</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="radio"
                name="dateFilter"
                value="past"
                checked={dateFilter === 'past'}
                onChange={(e) => setDateFilter(e.target.value as 'upcoming' | 'past' | 'all')}
              />
              <span>Past</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="radio"
                name="dateFilter"
                value="all"
                checked={dateFilter === 'all'}
                onChange={(e) => setDateFilter(e.target.value as 'upcoming' | 'past' | 'all')}
              />
              <span>All</span>
            </label>
          </div>
          <div></div>

          <label style={{ fontSize: '0.9rem', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Source:</label>
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            style={{
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.9rem',
              width: '100%'
            }}
          >
            <option value="all">All Sources</option>
            <option value="etix">Etix</option>
            <option value="eventim">EventIM</option>
            <option value="scraper">Scraper</option>
            <option value="ticketmaster">Ticketmaster</option>
            <option value="none">No Source</option>
          </select>
          <div></div>

          <label style={{ fontSize: '0.9rem', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Region:</label>
          <select
            value={regionFilter}
            onChange={(e) => setRegionFilter(e.target.value)}
            style={{
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.9rem',
              width: '100%'
            }}
          >
            <option value="all">All Regions</option>
            {regions.map((region) => (
              <option key={region.region_name} value={region.region_name}>
                {region.metro_name}
              </option>
            ))}
          </select>
          <div></div>

          <label style={{ fontSize: '0.9rem', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Venue:</label>
          <GroupedVenueSearchPicker
            id="events-toolbar-venue-picker"
            token={token || ''}
            regionFilter={regionFilter}
            placeholder={venueFilter === 'all' ? 'All venues — search to filter…' : 'Search venues…'}
            committedLabel={venueFilter === 'all' ? '' : venueFilterLabel || venueFilter}
            onPick={(v, displayLabel) => {
              setVenueFilter(v.id);
              setVenueFilterLabel(displayLabel);
            }}
            onClear={() => {
              setVenueFilter('all');
              setVenueFilterLabel('');
            }}
            clearAriaLabel="All venues"
          />
          <div></div>

          <label style={{ fontSize: '0.9rem', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.9rem',
              width: '100%'
            }}
          >
            <option value="all">All Statuses</option>
            <option value="none">No Status</option>
            {Array.from(new Set(events.map(e => e.status).filter(Boolean))).sort().map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
          <div></div>

          <div></div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={(e) => setShowDeleted(e.target.checked)}
              />
              <span>Show Deleted</span>
            </label>
            <button
              type="button"
              onClick={() => void handleLoadEvents()}
              disabled={!token || loading}
              className="btn-primary"
              style={{
                padding: '0.5rem 1rem',
                opacity: !token || loading ? 0.6 : 1,
                cursor: !token || loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Loading…' : 'Load events'}
            </button>
            <button
              type="button"
              onClick={() => {
                setDateFilter('upcoming');
                setSourceFilter('all');
                setRegionFilter('all');
                setVenueFilter('all');
                setVenueFilterLabel('');
                setStatusFilter('all');
                setSearchTerm('');
                setShowDeleted(false);
              }}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '0.9rem',
                cursor: 'pointer'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#5a6268'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#6c757d'}
            >
              Clear Filters
            </button>
          </div>
        </div>
        {!eventsListLoaded && (
          <p style={{ margin: '0.75rem 0 0', fontSize: '0.9rem', color: '#555', maxWidth: '52rem' }}>
            Loading the full event list can be slow. Set filters above, then click <strong>Load events</strong>.
            Choosing a <strong>region</strong> first limits what is fetched; <strong>All regions</strong> scans the entire table (up to the server limit).
            Use the venue search control to filter by any venue; click <strong>Load events</strong> again after changing filters.
          </p>
        )}
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
            {showRawData ? (
              <div style={{ display: 'flex', flexDirection: 'column', height: '100%', maxHeight: '80vh' }}>
                <h3>Raw Event Data</h3>
                <div style={{ flex: 1, overflow: 'auto', marginBottom: '1rem' }}>
                  <textarea
                    readOnly
                    value={JSON.stringify(editingEvent || formData, null, 2)}
                    style={{
                      width: '100%',
                      height: '100%',
                      minHeight: '400px',
                      fontFamily: 'monospace',
                      whiteSpace: 'pre',
                      padding: '1rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: '#f8f9fa'
                    }}
                  />
                </div>
                <div className="form-actions">
                  <button type="button" onClick={() => setShowRawData(false)} className="btn-primary">
                    Back
                  </button>
                </div>
              </div>
            ) : (
              <>
                <h3>{editingEvent ? 'Edit Event' : 'Create Event'}</h3>
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                <label>Venue *</label>
                {formData.venue_id ? (
                  <div style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#333' }}>
                    <strong>Current:</strong>{' '}
                    {modalVenueDetail?.id === formData.venue_id
                      ? `${modalVenueDetail.name} (${modalVenueDetail.id})`
                      : `${formData.venue_id}`}
                    {modalVenueDetail?.venue_group_id ? (
                      <div style={{ marginTop: '0.25rem' }}>
                        <strong>Group:</strong> {modalVenueDetail.venue_group_id}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#666' }}>None selected</div>
                )}
                <GroupedVenueSearchPicker
                  id="events-modal-venue-picker"
                  token={token || ''}
                  regionFilter={regionFilter}
                  placeholder="Search venues by name or id…"
                  committedLabel=""
                  onPick={(v) => setFormData({ ...formData, venue_id: v.id })}
                />
              </div>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Event Date (YYYY-MM-DD) *</label>
                  <input
                    type="date"
                    value={formData.event_date || ''}
                    onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <input
                    type="text"
                    value={formData.status || ''}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date/Time</label>
                  <input
                    type="datetime-local"
                    value={formData.start_date_time ? formData.start_date_time.slice(0, 16) : ''}
                    onChange={(e) => {
                      const datetimeValue = e.target.value;
                      // Convert datetime-local format (YYYY-MM-DDTHH:mm) to RFC3339 with Z suffix
                      const rfc3339Value = datetimeValue ? `${datetimeValue}:00Z` : '';
                      setFormData({ ...formData, start_date_time: rfc3339Value });
                    }}
                  />
                </div>
                <div className="form-group">
                  <label>End Date/Time</label>
                  <input
                    type="datetime-local"
                    value={formData.end_date_time ? formData.end_date_time.slice(0, 16) : ''}
                    onChange={(e) => {
                      const datetimeValue = e.target.value;
                      // Convert datetime-local format (YYYY-MM-DDTHH:mm) to RFC3339 with Z suffix
                      const rfc3339Value = datetimeValue ? `${datetimeValue}:00Z` : '';
                      setFormData({ ...formData, end_date_time: rfc3339Value });
                    }}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Timezone</label>
                <input
                  type="text"
                  value={formData.timezone || ''}
                  onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                  placeholder="America/Chicago"
                />
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={() => setShowRawData(true)} 
                  style={{ marginRight: 'auto' }}
                  className="btn-secondary"
                >
                  Show Raw Data
                </button>
                <button type="button" onClick={() => setShowForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingEvent ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </>
        )}
          </div>
        </div>
      )}

      {showArtistsModal && (
        <div className="modal-overlay" onClick={() => setShowArtistsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <h3>Manage Artists for Event</h3>
            {error && <div className="error-banner" style={{ marginBottom: '1rem' }}>{error}</div>}
            {loadingArtists ? (
              <div>Loading artists...</div>
            ) : (
              <>
                <div style={{ marginBottom: '20px' }}>
                  <h4>Current Artists</h4>
                  {eventArtists.length === 0 ? (
                    <p>No artists assigned to this event.</p>
                  ) : (
                    <table className="data-table" style={{ marginBottom: '20px' }}>
                      <thead>
                        <tr>
                          <th>Artist</th>
                          <th>Role</th>
                          <th>Order</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {eventArtists
                          .sort((a, b) => a.order - b.order)
                          .map((ea) => (
                            <tr key={ea.artist_id}>
                              <td>{getArtistDisplayLabel(ea.artist_id, ea.artist_name)}</td>
                              <td>{ea.is_headliner ? 'Headliner' : 'Supporting'}</td>
                              <td>{ea.order}</td>
                              <td>
                                <button
                                  onClick={() => handleRemoveArtist(ea.artist_id)}
                                  className="btn-sm btn-danger"
                                >
                                  Remove
                                </button>
                                {!ea.is_headliner && (
                                  <button
                                    onClick={() => handleUpdateArtist(ea.artist_id, true, 0)}
                                    className="btn-sm"
                                    style={{ marginLeft: '8px' }}
                                  >
                                    Make Headliner
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  )}
                </div>
                <div>
                  <h4>Add Artist</h4>
                  <div className="form-group">
                    <AdminSearchPicker<Artist>
                      id="events-add-artist-picker"
                      placeholder="Search artists by name or id…"
                      committedLabel={
                        pendingAddArtist ? `${pendingAddArtist.name} — ${pendingAddArtist.id}` : ''
                      }
                      disabledIds={eventArtists.map((ea) => ea.artist_id)}
                      fetchPage={fetchArtistPickerPage}
                      getOptionId={(a) => a.id}
                      formatOptionLabel={(a) => `${a.name} — ${a.id}`}
                      onSelect={(a) => setPendingAddArtist(a)}
                      onClear={() => setPendingAddArtist(null)}
                    />
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                      <button
                        type="button"
                        disabled={!pendingAddArtist}
                        onClick={() => {
                          if (pendingAddArtist) {
                            void handleAddArtist(pendingAddArtist.id, false);
                            setPendingAddArtist(null);
                          }
                        }}
                        className="btn-sm"
                        style={{ flex: 1 }}
                      >
                        Add as Supporting
                      </button>
                      <button
                        type="button"
                        disabled={!pendingAddArtist}
                        onClick={() => {
                          if (pendingAddArtist) {
                            void handleAddArtist(pendingAddArtist.id, true);
                            setPendingAddArtist(null);
                          }
                        }}
                        className="btn-sm btn-primary"
                        style={{ flex: 1 }}
                      >
                        Add as Headliner
                      </button>
                    </div>
                  </div>
                </div>
                <div className="form-actions" style={{ marginTop: '20px' }}>
                  <button onClick={() => setShowArtistsModal(false)} className="btn-primary">
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th 
                onClick={() => handleSort('id')}
                style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
              >
                ID {getSortIcon('id') && <span className="sort-arrow">{getSortIcon('id')}</span>}
              </th>
              <th 
                onClick={() => handleSort('name')}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Name {getSortIcon('name') && <span className="sort-arrow">{getSortIcon('name')}</span>}
              </th>
              <th 
                onClick={() => handleSort('venue')}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Venue {getSortIcon('venue') && <span className="sort-arrow">{getSortIcon('venue')}</span>}
              </th>
              <th 
                onClick={() => handleSort('event_date')}
                style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
              >
                Event Date {getSortIcon('event_date') && <span className="sort-arrow">{getSortIcon('event_date')}</span>}
              </th>
              <th 
                onClick={() => handleSort('status')}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Status {getSortIcon('status') && <span className="sort-arrow">{getSortIcon('status')}</span>}
              </th>
              <th>Source</th>
              <th>Status</th>
              <th style={{ whiteSpace: 'nowrap' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {getFilteredAndSortedEvents().length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                  {loading
                    ? 'Loading...'
                    : !eventsListLoaded
                      ? 'Choose filters above, then click Load events to show results here.'
                      : 'No events found. Create your first event to get started.'}
                </td>
              </tr>
            ) : (
              getFilteredAndSortedEvents().map((event) => (
              <React.Fragment key={event.id}>
              <tr style={{ opacity: event.is_deleted ? 0.6 : 1 }}>
                <td style={{ whiteSpace: 'nowrap' }}>{event.id}</td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {event.name}
                    {isFieldProtected(event, 'name') && (
                      <span
                        title={`Protected field. Changed from "${getFieldUpdate(event, 'name')?.old_value}" to "${getFieldUpdate(event, 'name')?.new_value}" on ${new Date(getFieldUpdate(event, 'name')?.mod_date || '').toLocaleString()}`}
                        style={{ cursor: 'help', color: '#ff9800' }}
                      >
                        🔒
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {getEventVenueDisplay(event)}
                    {isFieldProtected(event, 'venue_id') && (
                      <span
                        title={`Protected field. Changed on ${new Date(getFieldUpdate(event, 'venue_id')?.mod_date || '').toLocaleString()}`}
                        style={{ cursor: 'help', color: '#ff9800' }}
                      >
                        🔒
                      </span>
                    )}
                  </div>
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {event.event_date}
                    {isFieldProtected(event, 'event_date') && (
                      <span
                        title={`Protected field. Changed from "${getFieldUpdate(event, 'event_date')?.old_value}" to "${getFieldUpdate(event, 'event_date')?.new_value}" on ${new Date(getFieldUpdate(event, 'event_date')?.mod_date || '').toLocaleString()}`}
                        style={{ cursor: 'help', color: '#ff9800' }}
                      >
                        🔒
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {event.status || '-'}
                    {isFieldProtected(event, 'status') && (
                      <span
                        title={`Protected field. Changed from "${getFieldUpdate(event, 'status')?.old_value}" to "${getFieldUpdate(event, 'status')?.new_value}" on ${new Date(getFieldUpdate(event, 'status')?.mod_date || '').toLocaleString()}`}
                        style={{ cursor: 'help', color: '#ff9800' }}
                      >
                        🔒
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  {event.source ? (
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                      backgroundColor: event.source === 'ticketmaster' ? '#e3f2fd' : event.source === 'scraper' ? '#e8f5e9' : '#fff3e0',
                      color: event.source === 'ticketmaster' ? '#1976d2' : event.source === 'scraper' ? '#2e7d32' : '#e65100',
                      textTransform: 'capitalize'
                    }}>
                      {event.source === 'etix-affiliate' ? 'Etix-Affiliate' :
                       event.source === 'etix-public' ? 'Etix-Public' :
                       event.source === 'etix' ? 'Etix' :
                       event.source === 'eventim' ? 'EventIM' :
                       event.source === 'scraper' ? 'Scraper' :
                       event.source}
                    </span>
                  ) : (
                    <span style={{ color: '#999' }}>-</span>
                  )}
                </td>
                <td>
                  {event.is_deleted ? (
                    <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>Deleted</span>
                  ) : (
                    <span style={{ color: '#2e7d32' }}>Active</span>
                  )}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'nowrap', alignItems: 'center' }}>
                    <button type="button" onClick={() => handleEdit(event)} className="btn-sm">
                      <IconEdit /> Edit
                    </button>
                    <button
                      onClick={() => void handleManageArtists(event.id)}
                      className="btn-sm"
                    >
                      <IconUsers /> Artists
                    </button>
                    {event.source === 'ticketmaster' && (
                      <button
                        onClick={() => {
                          setEnrichmentEntityId(event.id);
                          setEnrichmentType('ticketmaster');
                          setShowEnrichmentEditor(true);
                        }}
                        className="btn-sm btn-enrichment"
                      >
                        <IconTicket /> Ticketmaster
                      </button>
                    )}
                    {(event.source === 'etix' || event.source === 'etix-affiliate' || event.source === 'etix-public') && (
                      <button
                        onClick={() => {
                          setEnrichmentEntityId(event.id);
                          setEnrichmentType('etix');
                          setShowEnrichmentEditor(true);
                        }}
                        className="btn-sm btn-enrichment"
                      >
                        <IconTicket /> Etix
                      </button>
                    )}
                    {event.source === 'eventim' && (
                      <button
                        onClick={() => {
                          setEnrichmentEntityId(event.id);
                          setEnrichmentType('eventim');
                          setShowEnrichmentEditor(true);
                        }}
                        className="btn-sm btn-enrichment"
                      >
                        <IconTicket /> EventIM
                      </button>
                    )}
                    {!event.source && (
                      <>
                        <button
                          onClick={() => {
                            setEnrichmentEntityId(event.id);
                            setEnrichmentType('ticketmaster');
                            setShowEnrichmentEditor(true);
                          }}
                          className="btn-sm btn-enrichment"
                        >
                          <IconTicket /> Add TM
                        </button>
                        <button
                          onClick={() => {
                            setEnrichmentEntityId(event.id);
                            setEnrichmentType('etix');
                            setShowEnrichmentEditor(true);
                          }}
                          className="btn-sm btn-enrichment"
                        >
                          <IconTicket /> Add Etix
                        </button>
                        <button
                          onClick={() => {
                            setEnrichmentEntityId(event.id);
                            setEnrichmentType('eventim');
                            setShowEnrichmentEditor(true);
                          }}
                          className="btn-sm btn-enrichment"
                        >
                          <IconTicket /> Add EventIM
                        </button>
                      </>
                    )}
                    {event.is_deleted ? (
                      <button
                        onClick={() => handleHardDelete(event.id)}
                        className="btn-sm btn-danger"
                      >
                        <IconDelete /> Hard Delete
                      </button>
                    ) : (
                      <button
                        onClick={() => handleDelete(event.id)}
                        className="btn-sm btn-danger"
                      >
                        <IconDelete /> Delete
                      </button>
                    )}
                  </div>
                  {event.manually_updated_fields && event.manually_updated_fields.length > 0 && (
                    <button
                      onClick={() => setExpandedEventId(expandedEventId === event.id ? null : event.id)}
                      style={{ marginTop: '0.5rem', fontSize: '0.875rem', padding: '0.25rem 0.5rem', display: 'block' }}
                      className="btn-sm"
                    >
                      {expandedEventId === event.id ? 'Hide' : 'Show'} Protected Fields ({event.manually_updated_fields.length})
                    </button>
                  )}
                </td>
              </tr>
              {expandedEventId === event.id && event.manually_updated_fields && event.manually_updated_fields.length > 0 && (
                <tr>
                  <td colSpan={8} style={{ padding: '1rem', backgroundColor: '#f9f9f9', borderTop: '2px solid #ddd' }}>
                    <div style={{ marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                      Protected Fields ({event.manually_updated_fields.length})
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
                      {event.manually_updated_fields.map((update, idx) => (
                        <div key={idx} style={{ padding: '0.75rem', backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '4px' }}>
                          <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: '#333' }}>{update.field_name}</div>
                          <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '0.5rem' }}>
                            <div><strong>Old:</strong> {String(update.old_value)}</div>
                            <div><strong>New:</strong> {String(update.new_value)}</div>
                            <div><strong>Date:</strong> {new Date(update.mod_date).toLocaleString()}</div>
                          </div>
                          <button
                            onClick={() => handleUnlockField(event.id, update.field_name)}
                            disabled={unlockingField?.eventId === event.id && unlockingField?.fieldName === update.field_name}
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                            className="btn-sm"
                          >
                            {unlockingField?.eventId === event.id && unlockingField?.fieldName === update.field_name ? 'Unlocking...' : 'Unlock'}
                          </button>
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              )}
              </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showEnrichmentEditor && (
        <EnrichmentEditor
          entityType="event"
          entityId={enrichmentEntityId}
          enrichmentType={enrichmentType}
          onClose={() => {
            setShowEnrichmentEditor(false);
            setEnrichmentEntityId('');
          }}
        />
      )}
    </PageLayout>
  );
};

export default EventsPage;
